/**
 * The admin panel's read model for one user, plus the pure rules that turn it
 * into something a support person can act on.
 *
 * The payload itself is assembled server-side by the `admin-user-details` edge
 * function (service role — none of these tables are readable by an admin over
 * RLS). Everything in this file is deliberately free of I/O so the "is this
 * user in trouble" rules can be unit tested, and so the shapes stay one
 * definition shared by the function's contract and the screen that renders it.
 */

import { APP_DISPLAY_NAMES, isAppId, type AppId } from "./appIdentity";

/* ------------------------------------------------------------------ *
 * Payload shapes — mirror of what admin-user-details returns.
 * ------------------------------------------------------------------ */

/**
 * Which app a device registration or OTA event came from.
 *
 * Every row has one since migration 20261021140000 (default 'crew'). Typed as
 * a plain string because it is read straight from the database, and optional
 * because it can be missing: the web page can be deployed before the edge
 * function that starts returning it. Read it through `appOf`, never directly.
 */
export interface FromApp {
  app?: string;
}

const CREW_APP: AppId = "crew";

/** The app of a row; one without (an older payload) is the crew app. */
export function appOf(row: FromApp): string {
  return row.app ?? CREW_APP;
}

/** "PROduce Crew" / "PROduce Producer"; an unknown id is shown as it is. */
export function appLabel(app: string): string {
  return isAppId(app) ? APP_DISPLAY_NAMES[app] : app;
}

export interface AdminUserProfile {
  id: string;
  email: string;
  full_name: string | null;
  company_name: string | null;
  phone: string | null;
  created_at: string;
  email_verified_at: string | null;
  guide_completed: boolean;
  briefing_guide_completed: boolean;
  manual_producer_access: boolean | null;
}

export interface AdminUserSubscription {
  plan_type: string | null;
  plan_tier: string | null;
  plan_status: string;
  subscription_start: string | null;
  subscription_end: string | null;
  trial_ends_at: string | null;
  stripe_customer_id: string | null;
  discount_code_used: string | null;
  discount_end_date: string | null;
}

/** One push registration — a device that can receive notifications. */
export interface AdminUserDevice extends FromApp {
  platform: string | null;
  client_type: string | null;
  native_platform: string | null;
  device_label: string | null;
  created_at: string;
}

export interface AdminCrewSettings {
  location_tracking_enabled: boolean | null;
  background_tracking_enabled: boolean | null;
  share_location_with_producer: boolean | null;
  show_other_crew_on_map: boolean | null;
  live_tracker_enabled: boolean | null;
  notify_new_assignment: boolean | null;
  notify_updated_assignment: boolean | null;
  notify_starting_soon: boolean | null;
  notify_announcements: boolean | null;
  notify_weather: boolean | null;
  reminder_minutes: number | null;
  app_version: string | null;
  platform: string | null;
  last_sync_at: string | null;
}

export interface AdminProducerSettings {
  notify_new_assignment: boolean | null;
  notify_updated_assignment: boolean | null;
  notify_deleted_assignment: boolean | null;
  notify_starting_soon: boolean | null;
  notify_announcements: boolean | null;
  notify_weather: boolean | null;
  notify_location_alerts: boolean | null;
  quiet_hours_enabled: boolean | null;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  location_tracking_enabled: boolean | null;
  location_tracking_during_assignments_only: boolean | null;
  highlight_crew_far_from_stage: boolean | null;
  tracking_accuracy: string | null;
  tracking_frequency: string | null;
  offline_cache_maps: boolean | null;
  offline_download_assignments: boolean | null;
  offline_download_lineup: boolean | null;
}

/**
 * A crew member as invited to one production, with invite-mail health.
 *
 * `crew_members` holds one row per person per festival day; this is the person,
 * with the days and functions folded in. Counting rows instead of people would
 * report a three-day festival as having three times its crew.
 */
export interface AdminProductionCrewMember extends InviteTimestamps {
  name: string;
  email: string;
  /** Distinct roles across the days, e.g. ["Camera", "Media office"]. */
  functions: string[];
  /** Festival days this person is on, as yyyy-MM-dd. */
  days: string[];
  /** What the receiving mail server said, e.g. "Permanent/NoEmail: ...". */
  email_bounce_reason: string | null;
  invitation_count: number;
  /** They have a PROduce account (the invite was accepted). */
  has_account: boolean;
}

export interface AdminProduction {
  id: string;
  festival_name: string | null;
  status: string;
  festival_date_start: string | null;
  festival_date_end: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  last_edit_step: number | null;
  day_count: number;
  stage_names: string[];
  lineup_count: number;
  assignment_count: number;
  has_map: boolean;
  crew: AdminProductionCrewMember[];
}

export interface AdminCoProducer {
  briefing_id: string;
  /** Which production they co-produce — a bare id is useless in support. */
  festival_name: string | null;
  label: string;
  last_login_at: string | null;
  revoked_at: string | null;
}

/** A production this user was invited to as crew. */
export interface AdminCrewMembership extends InviteTimestamps {
  briefing_id: string;
  festival_name: string | null;
  producer_name: string | null;
  festival_date_start: string | null;
  functions: string[];
  days: string[];
  email_bounce_reason: string | null;
  invitation_count: number;
  has_account: boolean;
  hidden_by_crew: boolean;
}

/** An OTA update attempt reported by one of the native apps. */
export interface AdminUpdateEvent extends FromApp {
  event_type: string;
  error_message: string | null;
  app_version: string | null;
  platform: string | null;
  created_at: string;
}

export interface AdminUserDetails {
  profile: AdminUserProfile;
  roles: string[];
  last_sign_in_at: string | null;
  subscription: AdminUserSubscription | null;
  devices: AdminUserDevice[];
  producer: {
    settings: AdminProducerSettings | null;
    /** The most recent productions, capped — see `production_count`. */
    productions: AdminProduction[];
    /** Total productions owned, including any beyond the detailed list. */
    production_count: number;
    co_producers: AdminCoProducer[];
  } | null;
  crew: {
    settings: AdminCrewSettings | null;
    location_sharing_consent: boolean | null;
    notification_lead_time: number | null;
    memberships: AdminCrewMembership[];
  } | null;
  /** Recent update attempts of any kind, for the timeline on the page. */
  update_events: AdminUpdateEvent[];
  /** Only the failures, over a wider window, for the signal that counts them. */
  update_errors: AdminUpdateEvent[];
  /** True when there are older failures than the ones returned. */
  update_errors_truncated: boolean;
}

/* ------------------------------------------------------------------ *
 * Derived facts.
 * ------------------------------------------------------------------ */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days between `iso` and `now`; null when the date is missing/unusable. */
export function daysSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.floor((now.getTime() - then) / DAY_MS);
}

export interface AppInstallState {
  /** The crew app has run at least once with this account. */
  hasApp: boolean;
  /** A native (iOS/Android) build, as opposed to the web app only. */
  isNative: boolean;
  appVersion: string | null;
  platform: string | null;
  lastSyncAt: string | null;
  daysSinceSync: number | null;
  /** At least one device can receive crew-app push notifications. */
  pushRegistered: boolean;
  /** Crew-app push registrations; the device list shows every app. */
  deviceCount: number;
}

/**
 * A device that is a native app (crew or producer) rather than the web app.
 *
 * Shared with the device list on the admin page so the per-device "App/Web"
 * badge and the "Type: Native app" summary above it cannot disagree.
 */
export function isNativeDevice(
  device: Pick<AdminUserDevice, "client_type" | "native_platform">,
): boolean {
  return device.client_type === "native" || device.native_platform !== null;
}

/**
 * Whether this user actually has the CREW app, and on what.
 *
 * `crew_settings` is written the first time the crew app boots with an
 * account, so an `app_version` there is the closest thing we have to
 * "installed". Push registrations answer a different question — a user can
 * have the app and still have declined notification permission — so both are
 * reported.
 *
 * Only crew-app registrations count (a web one is the retired crew PWA, so it
 * counts too). A producer who installed the producer app has not installed
 * the crew app, and their producer-app token does not deliver crew pushes —
 * counting it would silence the "never opened the crew app" and "no push
 * device" signals for exactly the person they are about.
 */
export function deriveAppInstallState(
  settings: Pick<AdminCrewSettings, "app_version" | "platform" | "last_sync_at"> | null,
  devices: AdminUserDevice[],
  now: Date = new Date(),
): AppInstallState {
  const crewDevices = devices.filter((device) => appOf(device) === CREW_APP);
  const nativeDevice = crewDevices.find(isNativeDevice);
  const platform =
    settings?.platform ?? nativeDevice?.native_platform ?? nativeDevice?.platform ?? null;

  return {
    hasApp: Boolean(settings?.app_version) || crewDevices.length > 0,
    isNative:
      Boolean(nativeDevice) || settings?.platform === "ios" || settings?.platform === "android",
    appVersion: settings?.app_version ?? null,
    platform,
    lastSyncAt: settings?.last_sync_at ?? null,
    daysSinceSync: daysSince(settings?.last_sync_at, now),
    pushRegistered: crewDevices.length > 0,
    deviceCount: crewDevices.length,
  };
}

export interface SettingToggle {
  label: string;
  enabled: boolean | null;
}

/**
 * Crew notification and location switches, in the order the app shows them.
 * A `null` means the user never touched the setting — rendered as the app's
 * default rather than as "off", because those are different support answers.
 */
export function summariseCrewSwitches(settings: AdminCrewSettings | null): SettingToggle[] {
  if (!settings) return [];
  return [
    { label: "New assignment", enabled: settings.notify_new_assignment },
    { label: "Updated assignment", enabled: settings.notify_updated_assignment },
    { label: "Starting soon", enabled: settings.notify_starting_soon },
    { label: "Announcements", enabled: settings.notify_announcements },
    { label: "Weather", enabled: settings.notify_weather },
    { label: "Location tracking", enabled: settings.location_tracking_enabled },
    { label: "Background tracking", enabled: settings.background_tracking_enabled },
    { label: "Share location with producer", enabled: settings.share_location_with_producer },
    { label: "Show other crew on map", enabled: settings.show_other_crew_on_map },
    { label: "Live tracker", enabled: settings.live_tracker_enabled },
  ];
}

/** The producer's equivalent list. */
export function summariseProducerSwitches(settings: AdminProducerSettings | null): SettingToggle[] {
  if (!settings) return [];
  return [
    { label: "New assignment", enabled: settings.notify_new_assignment },
    { label: "Updated assignment", enabled: settings.notify_updated_assignment },
    { label: "Deleted assignment", enabled: settings.notify_deleted_assignment },
    { label: "Starting soon", enabled: settings.notify_starting_soon },
    { label: "Announcements", enabled: settings.notify_announcements },
    { label: "Weather", enabled: settings.notify_weather },
    { label: "Location alerts", enabled: settings.notify_location_alerts },
    { label: "Quiet hours", enabled: settings.quiet_hours_enabled },
    { label: "Live tracking", enabled: settings.location_tracking_enabled },
    {
      label: "Track during assignments only",
      enabled: settings.location_tracking_during_assignments_only,
    },
    { label: "Highlight crew far from stage", enabled: settings.highlight_crew_far_from_stage },
    { label: "Offline maps", enabled: settings.offline_cache_maps },
    { label: "Offline assignments", enabled: settings.offline_download_assignments },
    { label: "Offline lineup", enabled: settings.offline_download_lineup },
  ];
}

/* ------------------------------------------------------------------ *
 * Invitations.
 * ------------------------------------------------------------------ */

/**
 * How far an invitation email got.
 *
 * All four timestamps come from the Resend webhook. Until 25 Aug 2026 that
 * webhook could not match a crew member at all — `send-crew-invite` stored no
 * message id and set no tag — so every column was null for all 106 invitations
 * ever sent, and the badge deliberately stopped at "Sent". Both ends are wired
 * up now, so the later states appear on their own; invitations sent BEFORE
 * that date will stay on "Sent" forever, because nothing was ever recorded
 * about them.
 *
 * The ladder is ordered by what a support person needs to see first. A bounce
 * outranks a delivery: a re-invite clears the previous state, so the two
 * cannot both describe the current attempt — and if they somehow do, "it
 * bounced" is the answer worth showing.
 */
export type InviteStatus =
  | "not-invited"
  | "sent"
  | "bounced"
  | "complained"
  | "delivered"
  | "opened";

export interface InviteTimestamps {
  invited_at: string | null;
  email_delivered_at: string | null;
  email_opened_at: string | null;
  email_bounced_at: string | null;
  email_complained_at: string | null;
  /**
   * Whether this send was instrumented at all. False for invitations from
   * before delivery tracking existed: no webhook will ever arrive for them.
   * A boolean rather than the Resend message id — the page needs the fact,
   * not the handle.
   */
  has_delivery_tracking?: boolean;
}

export function inviteStatus(member: InviteTimestamps): InviteStatus {
  if (member.email_bounced_at) return "bounced";
  if (member.email_complained_at) return "complained";
  if (member.email_opened_at) return "opened";
  if (member.email_delivered_at) return "delivered";
  if (member.invited_at) return "sent";
  return "not-invited";
}

/**
 * How each status reads and whether it counts as a failure.
 *
 * One table, not a switch plus a predicate plus a ternary in the component.
 * The sibling module `apps/web/src/lib/crewTrackingStatus.ts` documents what
 * that costs: a new status compiles cleanly and silently renders neutral. It
 * happened here immediately — "Marked as spam" fell through a component
 * ternary into the same grey as "Sent", which nobody chose. `Record` over the
 * union means the next status cannot be added without answering both columns.
 */
const INVITE_UI: Record<InviteStatus, { label: string; isProblem: boolean }> = {
  "not-invited": { label: "Not invited yet", isProblem: false },
  sent: { label: "Sent", isProblem: false },
  bounced: { label: "Bounced", isProblem: true },
  complained: { label: "Marked as spam", isProblem: true },
  delivered: { label: "Delivered", isProblem: false },
  opened: { label: "Opened", isProblem: false },
};

/** Short human label for an invite status. */
export function inviteStatusLabel(status: InviteStatus): string {
  return INVITE_UI[status].label;
}

/**
 * True when this status is something an admin should act on.
 *
 * Both a bounce and a spam complaint qualify, for different reasons: the first
 * never arrived, the second arrived and was rejected by the person. Either way
 * the invitation will not do its job, and further mail to that address is at
 * risk. Deliberately one predicate — the first version left "Marked as spam"
 * to a component ternary, which rendered it in the same neutral grey as "Sent".
 */
export function isInviteProblem(status: InviteStatus): boolean {
  return INVITE_UI[status].isProblem;
}

/**
 * Why an invitation can sit on "Sent" forever, or undefined when it cannot.
 *
 * Data-driven rather than a date comparison. A cut-off date would be wrong for
 * the invitations sent on the release day itself before the deploy landed, and
 * would still be sitting in the code a year later. "Was a message id recorded
 * for this send" answers the same question from the row, and goes quiet on its
 * own as soon as sends are instrumented.
 */
export function inviteTrackingCaveat(member: InviteTimestamps): string | undefined {
  if (inviteStatus(member) !== "sent") return undefined;
  if (member.has_delivery_tracking) return undefined;
  return "Sent before delivery tracking existed, so nothing was ever recorded about this one.";
}

/**
 * The plan label an admin sees for a subscription row.
 *
 * The "lifetime free" rule — a discount code with no end date — is not stored
 * as a plan type; it is inferred. That inference was written out three times in
 * the user list and would have been a fourth here, so it lives in one place.
 * Returns null when there is nothing meaningful to show, because the callers
 * disagree on whether that should read "Free" or a dash.
 */
export function describeSubscriptionPlan(
  subscription: Pick<
    AdminUserSubscription,
    "plan_type" | "discount_code_used" | "discount_end_date"
  > | null,
): string | null {
  if (!subscription) return null;
  if (subscription.discount_code_used && !subscription.discount_end_date) return "Lifetime Free";
  if (!subscription.plan_type) return null;
  return subscription.plan_type.charAt(0).toUpperCase() + subscription.plan_type.slice(1);
}

/* ------------------------------------------------------------------ *
 * Signals.
 * ------------------------------------------------------------------ */

export type SignalLevel = "warning" | "info";

export interface UserSignal {
  /** Stable key so the list can be rendered and tested without matching prose. */
  id: string;
  level: SignalLevel;
  /** One-line summary, including the count. */
  message: string;
  /** The rows behind the count: what, where, when. Capped — see DETAIL_CAP. */
  details?: string[];
  /** Why the signal may not mean what it looks like. Rendered smaller. */
  hint?: string;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "3 Aug 2026" in UTC. Written out rather than via `toLocaleDateString` so a
 * signal line reads the same on every machine — and so the tests do not depend
 * on the runner's locale or timezone.
 */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "date unknown";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "date unknown";
  const d = new Date(ms);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

const STALE_APP_DAYS = 30;
const STALLED_DRAFT_DAYS = 21;
const REPEAT_INVITE_THRESHOLD = 3;
const UNACCEPTED_INVITE_DAYS = 7;

/**
 * One rule = one signal. Split this way so each can be tested on its own and
 * so adding a rule cannot disturb the others — `buildUserSignals` only orders
 * and filters what the rules return.
 */
type SignalRule = (details: AdminUserDetails, now: Date) => UserSignal | null;

/** "Is this person crew" — by role, or by having been invited as one. */
function looksLikeCrew(details: AdminUserDetails): boolean {
  return details.roles.includes("crew") || (details.crew?.memberships.length ?? 0) > 0;
}

const emailUnverified: SignalRule = (details) =>
  details.profile.email_verified_at
    ? null
    : {
        id: "email-unverified",
        level: "info",
        message: "Email address has never been verified.",
      };

const appMissing: SignalRule = (details, now) => {
  if (!looksLikeCrew(details)) return null;
  const install = deriveAppInstallState(details.crew?.settings ?? null, details.devices, now);
  if (install.hasApp) return null;
  return {
    id: "no-app",
    level: "warning",
    message: "Never opened the crew app — no app version and no registered crew-app device.",
  };
};

const noPushDevice: SignalRule = (details, now) => {
  if (!looksLikeCrew(details)) return null;
  const install = deriveAppInstallState(details.crew?.settings ?? null, details.devices, now);
  if (!install.hasApp || install.pushRegistered) return null;
  return {
    id: "no-push-device",
    level: "warning",
    message: "No crew-app device registered for push — this user receives no crew notifications.",
  };
};

const staleApp: SignalRule = (details, now) => {
  const install = deriveAppInstallState(details.crew?.settings ?? null, details.devices, now);
  if (install.daysSinceSync === null || install.daysSinceSync < STALE_APP_DAYS) return null;
  return {
    id: "stale-app",
    level: "info",
    message: `App last synced ${install.daysSinceSync} days ago.`,
  };
};

/**
 * `expo_update_events` rows carrying an error, grouped by what went wrong.
 *
 * Eleven identical "Failed to load all assets" lines say no more than one
 * does. Grouping by message and build turns the count into something a support
 * person can act on: which app, which build, which error, and when it last
 * happened. The app is part of the build: crew v1.0 and producer v1.0 are two
 * different binaries with two different OTA channels.
 */
function groupFailures(failures: AdminUpdateEvent[]): string[] {
  const groups = new Map<
    string,
    {
      count: number;
      last: string;
      app: string;
      version: string | null;
      platform: string | null;
      message: string;
    }
  >();

  for (const failure of failures) {
    const message = failure.error_message ?? "Unknown error";
    const app = appOf(failure);
    const key = `${message}|${app}|${failure.app_version ?? ""}|${failure.platform ?? ""}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      if (failure.created_at > existing.last) existing.last = failure.created_at;
    } else {
      groups.set(key, {
        count: 1,
        last: failure.created_at,
        app,
        version: failure.app_version,
        platform: failure.platform,
        message,
      });
    }
  }

  return [...groups.values()]
    .sort((a, b) => b.last.localeCompare(a.last))
    .map((group) => {
      const build = [appLabel(group.app), group.version && `v${group.version}`, group.platform]
        .filter(Boolean)
        .join(" ");
      const times = group.count === 1 ? "" : `${group.count}× `;
      return `${times}${group.message}${build ? ` — ${build}` : ""} — last on ${shortDate(group.last)}`;
    });
}

const updateTruncationHint = (details: AdminUserDetails): string | undefined =>
  details.update_errors_truncated === true
    ? "Only the most recent failures are listed; there may be older ones."
    : undefined;

/**
 * The payload details only the most recent productions (`PRODUCTION_LIMIT` in
 * the edge function), so any rule that counts across them is counting a
 * sample. The page says so for the production list itself; a signal that
 * reports "2 crew members" as if it were the whole picture would quietly
 * undo that honesty.
 */
function productionTruncationHint(details: AdminUserDetails): string | undefined {
  const producer = details.producer;
  if (!producer || producer.production_count <= producer.productions.length) return undefined;
  return `Counted over the ${producer.productions.length} most recent of ${producer.production_count} productions.`;
}

/**
 * An update that was found but could not be installed. This is the real
 * problem: the phone is stuck on an old build.
 */
const updateInstallFailures: SignalRule = (details) => {
  const failures = (details.update_errors ?? []).filter(
    (event) => event.event_type === "download_error",
  );
  if (failures.length === 0) return null;
  return {
    id: "update-install-errors",
    level: "warning",
    message: `${failures.length} app ${plural(failures.length, "update", "updates")} failed to install.`,
    details: groupFailures(failures),
    hint: updateTruncationHint(details),
  };
};

/**
 * The app could not even ask whether an update exists.
 *
 * Kept apart from install failures and kept out of the warnings on purpose:
 * these are almost always "no signal" — a phone on a festival site times out
 * constantly, and one user's 55 check errors were all network timeouts. Rolled
 * into one red "77 failed app updates" it buried the 22 that mattered.
 */
const updateCheckFailures: SignalRule = (details) => {
  const failures = (details.update_errors ?? []).filter(
    (event) => event.event_type !== "download_error",
  );
  if (failures.length === 0) return null;
  return {
    id: "update-check-errors",
    level: "info",
    message: `${failures.length} update ${plural(failures.length, "check", "checks")} failed to reach the server — usually a bad connection.`,
    details: groupFailures(failures),
    hint: updateTruncationHint(details),
  };
};

/** Producer side: a published production nobody was invited to. */
const publishedWithoutCrew: SignalRule = (details) => {
  const empty = (details.producer?.productions ?? []).filter(
    (production) => production.status === "published" && production.crew.length === 0,
  );
  if (empty.length === 0) return null;
  return {
    id: "published-without-crew",
    level: "warning",
    message: `${empty.length} published ${plural(empty.length, "production has", "productions have")} no crew.`,
    details: empty.map((p) => p.festival_name || "Untitled production"),
    hint: productionTruncationHint(details),
  };
};

/** Producer side: a draft that has not moved in weeks. */
const stalledDrafts: SignalRule = (details, now) => {
  const stalled = (details.producer?.productions ?? [])
    .map((production) => ({ production, idle: daysSince(production.updated_at, now) }))
    .filter(
      (row) =>
        row.production.status !== "published" &&
        row.idle !== null &&
        row.idle >= STALLED_DRAFT_DAYS,
    );
  if (stalled.length === 0) return null;
  return {
    id: "stalled-drafts",
    level: "info",
    message: `${stalled.length} draft ${plural(stalled.length, "production has", "productions have")} not been touched for ${STALLED_DRAFT_DAYS}+ days.`,
    details: stalled.map(
      ({ production, idle }) =>
        `${production.festival_name || "Untitled production"} — last edited ${shortDate(production.updated_at)} (${idle} days ago)`,
    ),
    hint: productionTruncationHint(details),
  };
};

/**
 * Producer side: crew who were invited a while ago and still have no account.
 *
 * Complements `bouncedInvites` rather than replacing it. A bounce is proof the
 * message never landed; this rule catches the quieter case where it landed and
 * nothing happened. (An earlier version of this comment said delivery was not
 * tracked at all — that was true until the three links were joined.)
 */
const invitesNotAccepted: SignalRule = (details, now) => {
  const pending: string[] = [];
  for (const production of details.producer?.productions ?? []) {
    for (const member of production.crew) {
      if (member.has_account || !member.invited_at) continue;
      const age = daysSince(member.invited_at, now);
      if (age === null || age < UNACCEPTED_INVITE_DAYS) continue;
      pending.push(
        `${production.festival_name || "Untitled production"} — ${member.name} (${member.email}) — invited ${shortDate(member.invited_at)}, ${age} days ago`,
      );
    }
  }
  if (pending.length === 0) return null;
  return {
    id: "invites-not-accepted",
    level: "warning",
    message: `${pending.length} invited crew ${plural(pending.length, "member has", "members have")} still not created an account.`,
    details: pending,
    hint: productionTruncationHint(details),
  };
};

/**
 * An invitation that demonstrably never arrived.
 *
 * This is the rule the old "never delivered" one wanted to be. That one fired
 * for everyone because delivery was not tracked at all; this one only fires on
 * a bounce the mail server actually reported, and carries the reason — "no
 * such user" and "mailbox full" send support in different directions.
 */
const bouncedInvites: SignalRule = (details) => {
  const bounced: string[] = [];
  for (const production of details.producer?.productions ?? []) {
    for (const member of production.crew) {
      if (!member.email_bounced_at) continue;
      bounced.push(
        `${production.festival_name || "Untitled production"} — ${member.name} (${member.email}) — ${member.email_bounce_reason ?? "bounced"} on ${shortDate(member.email_bounced_at)}`,
      );
    }
  }
  if (bounced.length === 0) return null;
  return {
    id: "invites-bounced",
    level: "warning",
    message: `${bounced.length} crew ${plural(bounced.length, "invitation", "invitations")} bounced — the address did not accept it.`,
    details: bounced,
    hint: productionTruncationHint(details),
  };
};

/** The crew member's own side of the same thing. */
const ownInviteBounced: SignalRule = (details) => {
  const bounced = (details.crew?.memberships ?? []).filter((m) => m.email_bounced_at);
  if (bounced.length === 0) return null;
  return {
    id: "own-invite-bounced",
    level: "warning",
    message: `${bounced.length} ${plural(bounced.length, "invitation", "invitations")} to this address bounced.`,
    details: bounced.map(
      (m) =>
        `${m.festival_name || "Untitled production"} — ${m.email_bounce_reason ?? "bounced"} on ${shortDate(m.email_bounced_at)}`,
    ),
  };
};

/** Producer side: someone who had to be invited again and again. */
const repeatedInvites: SignalRule = (details) => {
  const repeats: string[] = [];
  for (const production of details.producer?.productions ?? []) {
    for (const member of production.crew) {
      if (member.invitation_count < REPEAT_INVITE_THRESHOLD) continue;
      repeats.push(
        `${production.festival_name || "Untitled production"} — ${member.name} — sent ${member.invitation_count}×`,
      );
    }
  }
  if (repeats.length === 0) return null;
  return {
    id: "invites-repeated",
    level: "info",
    message: `${repeats.length} crew ${plural(repeats.length, "member was", "members were")} invited ${REPEAT_INVITE_THRESHOLD}+ times.`,
    details: repeats,
    hint: productionTruncationHint(details),
  };
};

/** Crew side: productions this person hid from their own app. */
const hiddenProductions: SignalRule = (details) => {
  const hidden = (details.crew?.memberships ?? []).filter(
    (membership) => membership.hidden_by_crew,
  );
  if (hidden.length === 0) return null;
  return {
    id: "hidden-productions",
    level: "info",
    message: `${hidden.length} ${plural(hidden.length, "production is", "productions are")} hidden by this crew member.`,
    details: hidden.map(
      (membership) =>
        `${membership.festival_name || "Untitled production"}${
          membership.producer_name ? ` — producer ${membership.producer_name}` : ""
        }`,
    ),
  };
};

const RULES: SignalRule[] = [
  appMissing,
  noPushDevice,
  updateInstallFailures,
  bouncedInvites,
  ownInviteBounced,
  invitesNotAccepted,
  publishedWithoutCrew,
  emailUnverified,
  staleApp,
  stalledDrafts,
  repeatedInvites,
  hiddenProductions,
  updateCheckFailures,
];

/**
 * The "why is this user having a bad time" list: warnings first, then notes.
 *
 * Deliberately conservative — a signal that fires for every healthy account is
 * noise that makes the real ones invisible. Every rule that reports a count
 * also reports the rows behind it, because a bare number sends support back to
 * the database.
 */
export function buildUserSignals(details: AdminUserDetails, now: Date = new Date()): UserSignal[] {
  const signals = RULES.map((rule) => rule(details, now)).filter(
    (signal): signal is UserSignal => signal !== null,
  );
  return [
    ...signals.filter((signal) => signal.level === "warning"),
    ...signals.filter((signal) => signal.level === "info"),
  ];
}
